import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ExternalLink, Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { branchInput, type OnboardingStatus } from '@barbeiropro/contracts';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { AddressFields } from '@/components/shared/address-fields';
import { Field } from '@/components/shared/field';
import { MaskedInput } from '@/components/shared/masked-input';
import { TimeSelect } from '@/components/shared/time-select';
import { PageHeader } from '@/components/shared/page-header';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { api, errorMessage } from '@/lib/api';
import { parseMoney } from '@/lib/format';
import { useBranches } from '@/lib/org';
import { useSession } from '@/lib/session';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { onboardingStateKey, useOnboardingState } from './use-dashboard';
import { cn } from '@/lib/utils';

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function Step({ n, title, done, open, children, description }: { n: number; title: string; done: boolean; open: boolean; description: string; children: ReactNode }) {
  return (
    <li className={cn('rounded-lg border bg-card', open && 'ring-1 ring-primary/30')}>
      <div className="flex items-start gap-4 px-5 py-4">
        <span
          className={cn(
            'grid size-8 shrink-0 place-items-center rounded-full border font-display text-lg font-bold',
            done ? 'border-primary bg-primary text-primary-foreground' : 'text-muted-foreground',
          )}
          aria-hidden
        >
          {done ? <Check className="size-4" /> : n}
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold">
            {title} {done && <span className="sr-only">(concluído)</span>}
          </h2>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      {open && <div className="border-t px-5 py-5">{children}</div>}
    </li>
  );
}

function BranchStep({ onDone }: { onDone: () => void }) {
  const form = useForm<z.input<typeof branchInput>>({ resolver: zodResolver(branchInput), defaultValues: { name: 'Unidade principal', active: true } });
  const save = form.handleSubmit(async (v) => {
    try {
      await api.post('/org/branches', v);
      toast.success('Unidade salva');
      onDone();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  });
  return (
    <form onSubmit={save} className="grid gap-4" noValidate>
      <Field id="branch-name" label="Nome da unidade" error={form.formState.errors.name?.message}>
        <Input id="branch-name" {...form.register('name')} />
      </Field>
      <AddressFields form={form} />
      <Button type="submit" className="justify-self-start" disabled={form.formState.isSubmitting}>
        Salvar unidade
      </Button>
    </form>
  );
}

function HoursStep({ onDone }: { onDone: () => void }) {
  const { data: branches } = useBranches();
  const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5, 6]);
  const [opensAt, setOpensAt] = useState('09:00');
  const [closesAt, setClosesAt] = useState('19:00');
  const save = useMutation({
    mutationFn: () =>
      api.put('/org/hours', { branchId: branches![0]!.id, intervals: days.map((weekday) => ({ weekday, opensAt, closesAt })) }),
    onSuccess: () => {
      toast.success('Horário salvo');
      onDone();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (!branches?.length) return <p className="text-sm text-muted-foreground">Cadastre a unidade primeiro.</p>;
  return (
    <div className="grid gap-4">
      <fieldset className="flex flex-wrap gap-2">
        <legend className="mb-2 text-sm font-medium">Dias de funcionamento</legend>
        {WEEKDAYS.map((d, i) => (
          <label key={d} className={cn('flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm', days.includes(i) && 'border-primary bg-accent')}>
            <Checkbox checked={days.includes(i)} onCheckedChange={(c) => setDays((s) => (c ? [...s, i].sort() : s.filter((x) => x !== i)))} />
            {d}
          </label>
        ))}
      </fieldset>
      <div className="flex flex-wrap gap-4">
        <Field id="opens" label="Abre às">
          <TimeSelect id="opens" value={opensAt} onChange={(e) => setOpensAt(e.target.value)} className="w-28" />
        </Field>
        <Field id="closes" label="Fecha às">
          <TimeSelect id="closes" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} className="w-28" />
        </Field>
      </div>
      <p className="text-sm text-muted-foreground">Pausas de almoço, feriados e horários por profissional ficam em Configurações.</p>
      <Button className="justify-self-start" disabled={!days.length || save.isPending} onClick={() => save.mutate()}>
        Salvar horário
      </Button>
    </div>
  );
}

const servicesForm = z.object({
  items: z.array(z.object({ name: z.string().trim().min(2, 'Informe o nome'), duration: z.coerce.number().int().min(5).max(480), price: z.string().min(1, 'Informe o preço') })).min(1),
});

function ServicesStep({ onDone }: { onDone: () => void }) {
  const form = useForm<z.input<typeof servicesForm>>({
    resolver: zodResolver(servicesForm),
    defaultValues: {
      items: [
        { name: 'Corte masculino', duration: 40, price: '60,00' },
        { name: 'Barba', duration: 30, price: '45,00' },
        { name: 'Corte e barba', duration: 70, price: '95,00' },
      ],
    },
  });
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'items' });
  const save = form.handleSubmit(async (v) => {
    try {
      for (const i of (v as z.output<typeof servicesForm>).items) {
        await api.post('/services', { name: i.name, durationMinutes: i.duration, priceCents: parseMoney(i.price) });
      }
      toast.success('Serviços salvos');
      onDone();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  });
  return (
    <form onSubmit={save} className="grid gap-3" noValidate>
      {fields.map((f, idx) => (
        <div key={f.id} className="grid grid-cols-[1fr_6rem_7rem_auto] items-end gap-2">
          <Field id={`s-${idx}-name`} label="Serviço" error={form.formState.errors.items?.[idx]?.name?.message}>
            <Input id={`s-${idx}-name`} {...form.register(`items.${idx}.name`)} />
          </Field>
          <Field id={`s-${idx}-dur`} label="Minutos">
            <Input id={`s-${idx}-dur`} type="number" min={5} step={5} {...form.register(`items.${idx}.duration`)} />
          </Field>
          <Field id={`s-${idx}-price`} label="Preço (R$)" error={form.formState.errors.items?.[idx]?.price?.message}>
            <MaskedInput mask="money" id={`s-${idx}-price`} {...form.register(`items.${idx}.price`)} />
          </Field>
          <Button type="button" variant="ghost" size="icon" aria-label="Remover serviço" onClick={() => remove(idx)} disabled={fields.length === 1}>
            <Trash2 className="size-4" />
          </Button>
        </div>
      ))}
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={() => append({ name: '', duration: 30, price: '' })}>
          <Plus className="size-4" /> Adicionar serviço
        </Button>
        <Button type="submit" disabled={form.formState.isSubmitting}>
          Salvar serviços
        </Button>
      </div>
    </form>
  );
}

function BarbersStep({ onDone }: { onDone: () => void }) {
  const { data: branches } = useBranches();
  const [names, setNames] = useState<string[]>(['']);
  const save = useMutation({
    mutationFn: async () => {
      const services = await api.get<{ data: { id: string }[] }>('/services', { pageSize: 100 });
      for (const displayName of names.map((n) => n.trim()).filter((n) => n.length >= 2)) {
        const b = await api.post<{ id: string }>('/barbers', { displayName, branchId: branches![0]!.id });
        await api.put(`/barbers/${b.id}/services`, { items: services.data.map((s) => ({ serviceId: s.id })) });
      }
    },
    onSuccess: () => {
      toast.success('Equipe salva');
      onDone();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (!branches?.length) return <p className="text-sm text-muted-foreground">Cadastre a unidade primeiro.</p>;
  return (
    <div className="grid gap-3">
      {names.map((n, i) => (
        <Field key={i} id={`b-${i}`} label={`Profissional ${i + 1}`}>
          <Input id={`b-${i}`} value={n} onChange={(e) => setNames((s) => s.map((x, j) => (j === i ? e.target.value : x)))} />
        </Field>
      ))}
      <p className="text-sm text-muted-foreground">Todos os serviços ficam habilitados para cada profissional. Ajuste depois em Equipe.</p>
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={() => setNames((s) => [...s, ''])}>
          <Plus className="size-4" /> Adicionar profissional
        </Button>
        <Button disabled={save.isPending || !names.some((n) => n.trim().length >= 2)} onClick={() => save.mutate()}>
          Salvar equipe
        </Button>
      </div>
    </div>
  );
}

function PublishStep({ onDone }: { onDone: () => void }) {
  const { org } = useSession();
  const publish = useMutation({
    mutationFn: async () => {
      const pages = await api.get<{ id: string; slug: string }[]>('/site/pages');
      const home = pages.find((p) => p.slug === '');
      if (!home) throw new Error('Página inicial não encontrada');
      await api.post(`/site/pages/${home.id}/publish`);
    },
    onSuccess: () => {
      toast.success('Site publicado');
      onDone();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <div className="grid gap-3">
      <p className="text-sm">
        Seu site já vem com página inicial, serviços, equipe, endereço e agendamento online. Publique agora e ajuste textos e fotos no editor quando quiser.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => publish.mutate()} disabled={publish.isPending}>
          Publicar site
        </Button>
        <Button variant="outline" asChild>
          <Link to="/app/site">Abrir editor</Link>
        </Button>
        {org && (
          <Button variant="ghost" asChild>
            <a href={`/b/${org.organizationSlug}`} target="_blank" rel="noreferrer">
              Ver site <ExternalLink className="size-3.5" />
            </a>
          </Button>
        )}
      </div>
    </div>
  );
}

function DemoDataCard() {
  const { can } = useSession();
  const qc = useQueryClient();
  const state = useOnboardingState();
  if (!can('settings.manage') || !state.data) return null;
  const after = () => {
    qc.invalidateQueries();
  };
  const load = async () => {
    try {
      const r = await api.post<{ created: Record<string, number> }>('/onboarding/demo-data');
      toast.success(`Dados de exemplo carregados (${Object.values(r.created).reduce((a, b) => a + b, 0)} registros)`);
      after();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const remove = async () => {
    try {
      const r = await api.del<{ removed: Record<string, number> }>('/onboarding/demo-data');
      toast.success(`Dados de exemplo removidos (${Object.values(r.removed).reduce((a, b) => a + b, 0)} registros)`);
      after();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <section className="rounded-lg border bg-card p-5">
      <h2 className="font-semibold">Dados de exemplo</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Carregue clientes, serviços e agendamentos fictícios para conhecer o sistema. Eles são marcados e podem ser removidos a qualquer momento, sem afetar o que você cadastrou.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {state.data.demoData ? (
          <ConfirmDialog trigger={<Button variant="outline">Remover dados de exemplo</Button>} title="Remover dados de exemplo?" description="Somente os registros de exemplo são apagados. Seus dados reais permanecem." confirmLabel="Remover" onConfirm={remove} />
        ) : (
          <Button variant="outline" onClick={load}>
            Carregar dados de exemplo
          </Button>
        )}
      </div>
    </section>
  );
}

export function OnboardingPage() {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ['org', 'onboarding'], queryFn: () => api.get<OnboardingStatus>('/org/onboarding') });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['org'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
    qc.invalidateQueries({ queryKey: ['barbers'] });
    qc.invalidateQueries({ queryKey: onboardingStateKey });
  };
  if (status.isLoading) return <LoadingState />;
  if (status.error || !status.data) return <ErrorState error={status.error} onRetry={status.refetch} />;
  const s = status.data;
  const steps = [s.hasBranch, s.hasHours, s.hasServices, s.hasBarbers, s.sitePublished];
  const current = steps.findIndex((d) => !d);

  return (
    <div className="mx-auto grid max-w-3xl gap-6">
      <PageHeader title="Primeiros passos" description="Cinco passos para a barbearia receber o primeiro agendamento online." />
      <ol className="grid gap-3">
        <Step n={1} title="Unidade e endereço" description="Onde os clientes vão te encontrar." done={s.hasBranch} open={current === 0}>
          <BranchStep onDone={refresh} />
        </Step>
        <Step n={2} title="Horário de funcionamento" description="A agenda só oferece horários dentro do expediente." done={s.hasHours} open={current === 1}>
          <HoursStep onDone={refresh} />
        </Step>
        <Step n={3} title="Serviços e preços" description="O que o cliente pode agendar, com duração e valor." done={s.hasServices} open={current === 2}>
          <ServicesStep onDone={refresh} />
        </Step>
        <Step n={4} title="Equipe" description="Quem atende. Cada profissional tem a própria agenda." done={s.hasBarbers} open={current === 3}>
          <BarbersStep onDone={refresh} />
        </Step>
        <Step n={5} title="Publicar o site" description="Página com seus serviços, equipe e agendamento online." done={s.sitePublished} open={current === 4}>
          <PublishStep onDone={refresh} />
        </Step>
      </ol>
      <DemoDataCard />
      {current === -1 && (
        <div className="rounded-lg border bg-card p-5 text-center">
          <p className="font-medium">Tudo pronto. A barbearia já pode receber agendamentos.</p>
          <Button asChild className="mt-3">
            <Link to="/app">Ir para o painel</Link>
          </Button>
        </div>
      )}
    </div>
  );
}
